import { useEffect, useState } from 'react'

import { NotificationProvider } from '@joor/design-system'
import { ErrorBoundary } from 'react-error-boundary'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { isRestrictedNoAccessViewer } from 'utils/noAccessAccount'
import usePHPMixpanelTrackers from 'utils/phpTracking/usePHPMixpanelTrackers'
import {
  reportRenderErrorToDatadog,
  setLocationForDatadogRUM,
} from 'utils/sdks/datadog'
import { useHotjar } from 'utils/sdks/hotjar'
import { useLocalize } from 'utils/sdks/localize'
import { usePendo } from 'utils/sdks/pendo'
import { useZendesk } from 'utils/sdks/zendesk'
import { CurrentUserInfo } from 'utils/userInfo'

import { setCurrentUser } from 'store/currentUser/actions'
import { loadUserState } from 'store/utils/storage/actions'

import SessionExpiredModal from 'components/Error/SessionExpiredModal/SessionExpiredModal'
import ErrorPage from 'pages/ErrorPage'
import NoAccessAccountPage from 'pages/NoAccessAccountPage/NoAccessAccountPage'
import PageContent from 'pages/PageContent'
import PageNavbar from 'pages/PageNavbar'

import Notifications from './containers/Notifications/Notifications.container'

function BaseApp({ userInfo }: { userInfo: CurrentUserInfo }) {
  const dispatch = useDispatch()
  const location = useLocation()

  const [sessionExpiredModalVisible, setSessionExpireModalVisibility] =
    useState(false)

  useHotjar(userInfo)
  setLocationForDatadogRUM(location)
  usePHPMixpanelTrackers()
  usePendo(userInfo)
  useZendesk(userInfo)
  useLocalize(userInfo)

  useEffect(() => {
    dispatch(setCurrentUser(userInfo))
    dispatch(loadUserState(userInfo))
  }, [userInfo])

  useEffect(() => {
    const handler = () => {
      if (!sessionExpiredModalVisible && userInfo.isLoggedIn) {
        setSessionExpireModalVisibility(true)
      }
    }
    window.addEventListener('openSessionModal', handler)
    return () => window.removeEventListener('openSessionModal', handler)
  }, [sessionExpiredModalVisible, userInfo])

  const showNoAccessSwitcher = isRestrictedNoAccessViewer(userInfo)

  return (
    <NotificationProvider>
      <Notifications />
      <PageNavbar />
      {sessionExpiredModalVisible && (
        <SessionExpiredModal setVisibility={setSessionExpireModalVisibility} />
      )}
      <ErrorBoundary
        fallback={<ErrorPage extraMargin />}
        resetKeys={[location.key]}
        onError={reportRenderErrorToDatadog}
      >
        {showNoAccessSwitcher ? <NoAccessAccountPage /> : <PageContent />}
      </ErrorBoundary>
    </NotificationProvider>
  )
}

export default BaseApp
